import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect } from "react";
import { IChoiceGroupOption, ChoiceGroup, Text, IChoiceGroupProps } from "@fluentui/react";
import TagsInput, { defaultBreakKeysOnTagsInputs } from "../TagsInput";

import { v4 } from "uuid";

interface IAliasPosterOrReviewerTypeProps {
  id: string;
  initialSelected: "P" | "R";
}

const aliasDescriptionId = v4();

export interface ISLSearchAliasTypeRef {
  getAliasType: () => "P" | "R";
  reset: () => void;
}

const choiceGroupStyles: IChoiceGroupProps["styles"] = {
  root: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    alignContent: "center"
  },
  label: {
    margin: "0 8px 0 0"
  },
  flexContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr auto",
    gridColumnGap: "16px",
    marginTop: "-8px"
  }
};

const choicesForChoiceGroup: IChoiceGroupProps["options"] = [
  {
    key: "P",
    text: "Poster"
  },
  {
    key: "R",
    text: "Reviewer"
  }
];

const AliasPosterOrReviewerType = forwardRef((props: IAliasPosterOrReviewerTypeProps, ref) => {
  const [choice, setChoice] = useState<"P" | "R">(props.initialSelected);

  useEffect(() => {
    setChoice(props.initialSelected);
  }, [props.initialSelected]);

  useImperativeHandle(
    ref,
    (): ISLSearchAliasTypeRef => ({
      getAliasType() {
        return choice;
      },
      reset() {
        setChoice("P");
      }
    })
  );

  const onChange = (ev: any, option?: IChoiceGroupOption) => {
    const opt = option && option.key;
    setChoice(opt as "R" | "P");
  };

  return (
    <ChoiceGroup
      className="defaultChoiceGroup"
      styles={choiceGroupStyles}
      id={props.id}
      defaultSelectedKey={choice}
      options={choicesForChoiceGroup}
      label="Alias Type"
      onChange={onChange}
    />
  );
});

AliasPosterOrReviewerType.displayName = "AliasPosterOrReviewerType";

interface ISLSearchAliasInputProps {
  disabled?: boolean;
}

interface ISLSearchAliasInputSetter {
  aliases: string[];
  aliasType: "P" | "R";
}

export interface ISLSearchAliasInputRef {
  getAliases: () => string[];
  getAliasType: () => "P" | "R";
  setInitialAliases: (obj: ISLSearchAliasInputSetter) => void;
  reset: () => void;
}

const searchAliasId = v4();

const SLSearchAliasInput = forwardRef((props: ISLSearchAliasInputProps, ref: React.Ref<ISLSearchAliasInputRef>) => {
  const [aliases, setAliases] = useState<string[]>([]);
  const [aliasType, setAliasType] = useState<"P" | "R" | undefined>(undefined);

  const aliasTypeRef = useRef<ISLSearchAliasTypeRef>(null);

  useImperativeHandle(
    ref,
    (): ISLSearchAliasInputRef => ({
      getAliases() {
        return aliases;
      },
      getAliasType() {
        if (aliasTypeRef.current) {
          return aliasTypeRef.current.getAliasType();
        } else {
          return "P";
        }
      },
      setInitialAliases(obj) {
        if (obj.aliases.length !== 0) {
          setAliases(obj.aliases);
        }
        setAliasType(obj.aliasType);
      },
      reset() {
        setAliases([]);
        if (!aliasTypeRef.current) {
          setAliasType("P");
        } else {
          aliasTypeRef.current.reset();
        }
      }
    })
  );

  return (
    <React.Fragment>
      {aliasType ? (
        <AliasPosterOrReviewerType id={searchAliasId} ref={aliasTypeRef} initialSelected={aliasType} />
      ) : null}
      <TagsInput
        ariaLabelledBy={searchAliasId}
        ariaDescribedBy={aliasDescriptionId}
        value={aliases}
        onChange={(t: string[]) => setAliases(t.map((s) => s.toUpperCase()))}
        addKeys={defaultBreakKeysOnTagsInputs()}
        disabled={!!props.disabled}
        uppercase={true}
      />
      <Text
        id={aliasDescriptionId}
        nowrap
        variant="xSmall"
        block
        style={{
          marginTop: "4px",
          color: "var(--accent-font-color, #727272)"
        }}
      >
        Multiple alias enabled. Separate by semicolons.
      </Text>
    </React.Fragment>
  );
});

SLSearchAliasInput.displayName = "SLSearchAliasInput";

export default SLSearchAliasInput;
